/**
 * TravelRiderCard — Travel & Hospitality summary distilled from the artist's
 * onboarding questionnaire, surfaced BEFORE the customer confirms a booking.
 *
 * Data source: /api/artists/{id}/quote?city=X → response.rider
 *   Nothing new is asked of the artist here — the fields below are exact
 *   passthroughs from `artist_profiles.answers` populated during onboarding.
 *
 * Rendered:
 *   • Only when quote.is_outstation is true (local bookings don't need this)
 *   • Below the Outstation Terms card on the artist profile
 *   • And on the ReviewStep of the BookingFlow as a final read-only summary
 */
import React from "react";

const ROW = ({ label, value }) => {
  if (value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) return null;
  const display = Array.isArray(value) ? value.join(" · ") : String(value);
  return (
    <div className="rider-row" data-testid={`rider-row-${label.toLowerCase().replace(/\W+/g, "-")}`}>
      <div className="rider-row-label">{label}</div>
      <div className="rider-row-value">{display}</div>
    </div>
  );
};

export default function TravelRiderCard({ rider, artistCity, eventCity, compact = false }) {
  if (!rider || !rider.has_any) {
    // Graceful empty state — customer still knows travel is out-of-scope.
    return (
      <div className="rider-card rider-card-empty" data-testid="rider-card-empty">
        <div className="rider-head">
          <div>
            <div className="rider-head-title">Travel & Hospitality Requirements</div>
            <div className="rider-head-sub">The artist hasn't published a detailed rider yet — please coordinate directly.</div>
          </div>
        </div>
      </div>
    );
  }

  const hotelRequirement = {
    Always: "For every event",
    "Only for outstation events": "For out-of-town events",
    No: "Not required",
  }[rider.hotel_required] || rider.hotel_required;
  const travelPartySize = rider.travel_party_size === "Only me"
    ? "1 person (artist only)"
    : rider.travel_party_size
      ? `${rider.travel_party_size} people, including the artist`
      : null;
  const soundProvider = rider.sound_provider === "Customer"
    ? "Customer or venue"
    : rider.sound_provider;

  return (
    <div className={`rider-card ${compact ? "compact" : ""}`} data-testid="travel-rider-card">
      <div className="rider-head">
        <div>
          <div className="rider-head-title">Travel & Hospitality Requirements</div>
          <div className="rider-head-sub">
            {artistCity && eventCity
              ? <>Artist-declared preferences for this outstation booking · {artistCity} → {eventCity}</>
              : <>Artist-declared travel and hospitality preferences</>}
          </div>
        </div>
      </div>

      <div className="rider-grid">
        <ROW label="Travel mode" value={rider.travel_modes} />
        <ROW label="Flight class" value={rider.flight_class} />
        <ROW label="Train class" value={rider.train_class} />
        <ROW label="Local transport" value={rider.local_transport} />
        <ROW label="Accommodation needed" value={hotelRequirement} />
        <ROW label="Hotel category" value={rider.hotel_category} />
        <ROW label="Usual travel party size" value={travelPartySize} />
        <ROW label="Travel cost arrangement" value={rider.travel_charge_model} />
        <ROW label="Flat travel fee" value={rider.travel_flat_fee == null ? null : `₹${Number(rider.travel_flat_fee).toLocaleString("en-IN")}`} />
        <ROW label="Free travel distance" value={rider.travel_free_distance_km == null ? null : `${rider.travel_free_distance_km} km`} />
        <ROW label="Per-km travel fee" value={rider.travel_per_km_fee == null ? null : `₹${Number(rider.travel_per_km_fee).toLocaleString("en-IN")}/km`} />
        <ROW label="Food preference" value={rider.food_preference} />
        <ROW label="Hospitality" value={rider.hospitality_needs} />
        <ROW label="Green room" value={rider.green_room_required ? "Required" : null} />
        <ROW label="Sound system provided by" value={soundProvider} />
        <ROW label="Sound requirements" value={rider.sound_details} />
        <ROW label="Light requirements" value={rider.light_details} />
        <ROW
          label="Estimated technical setup cost (customer/venue arranged)"
          value={rider.technical_setup_estimate == null || rider.technical_setup_estimate === ""
            ? null
            : `₹${Number(rider.technical_setup_estimate).toLocaleString("en-IN")}`}
        />
        <ROW label="Technical notes" value={rider.technical_notes} />
        <ROW label="Travel notes" value={rider.travel_notes} />
        <ROW label="Additional conditions" value={rider.additional_conditions} />
      </div>

      <div className="rider-foot">
        These are the artist's declared preferences and setup notes. Package-specific terms show what is included and what must be arranged separately. Confirm anything unclear directly with the artist; the booking Tech Rider PDF, when available, contains the detailed technical setup.
      </div>
    </div>
  );
}
